import { Entity } from '@backstage/catalog-model';
import { useEntity, EntityRefLink } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 500,
    minWidth: 120,
  },
  owners: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const AWS_ACCOUNT_ID = 'temabit.com/aws-account-id';
const AWS_ACCOUNT_ALIAS = 'temabit.com/aws-account-alias';
const AWS_ACCOUNT_STATUS = 'temabit.com/aws-account-status';
const BUSINESS_OWNERS = 'temabit.com/business-owners';
const TECHNICAL_OWNERS = 'temabit.com/technical-owners';
const PROJECT_MANAGERS = 'temabit.com/project-managers';

/** Splits a comma-separated annotation into trimmed, non-empty tokens. */
const tokens = (raw: string | undefined): string[] =>
  (raw ?? '')
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

/**
 * A resolved catalog ref (e.g. `user:default/jane.doe`) always contains a `:`;
 * an unresolved raw email never does. That distinguishes clickable refs from
 * plain-text fallbacks without a fragile email regex.
 */
const isEntityRef = (value: string): boolean => value.includes(':');

const Owners = ({ label, values }: { label: string; values: string[] }) => {
  const classes = useStyles();
  if (values.length === 0) {
    return null;
  }
  return (
    <div className={classes.row}>
      <Typography variant="body2" className={classes.label}>
        {label}
      </Typography>
      <div className={classes.owners}>
        {values.map((value, index) => (
          <Typography variant="body2" component="span" key={value}>
            {isEntityRef(value) ? <EntityRefLink entityRef={value} /> : value}
            {index < values.length - 1 ? ',' : ''}
          </Typography>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value }: { label: string; value?: string }) => {
  const classes = useStyles();
  if (!value) {
    return null;
  }
  return (
    <div className={classes.row}>
      <Typography variant="body2" className={classes.label}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </div>
  );
};

/**
 * True when this card would render something: an `aws-account` Resource that
 * actually carries an account id.
 *
 * Exported so the entity page can gate the card's `<Grid item>` on exactly the
 * same condition the card itself applies (feature 0031). Testing only
 * `spec.type` there would emit an empty, spaced grid row for an aws-account
 * Resource whose id annotation is missing — the very thing the visibility work
 * exists to remove.
 */
export const hasAwsAccountCardData = (entity: Entity): boolean => {
  const type = (entity.spec as { type?: string } | undefined)?.type;
  return (
    type === 'aws-account' &&
    Boolean(entity.metadata.annotations?.[AWS_ACCOUNT_ID])
  );
};

/**
 * Entity card for `aws-account` Resources (feature 0009). Renders the account
 * id, alias, and status, plus the discovered owners as clickable `EntityRefLink`s
 * (falling back to plain text for owners that stayed as raw emails). Self-guards:
 * renders nothing unless the entity is an aws-account Resource.
 */
export const AwsAccountCard = () => {
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations ?? {};
  const accountId = annotations[AWS_ACCOUNT_ID];

  if (!hasAwsAccountCardData(entity)) {
    return null;
  }

  return (
    <InfoCard title="AWS Account">
      <Field label="Account ID" value={accountId} />
      <Field label="Alias" value={annotations[AWS_ACCOUNT_ALIAS]} />
      <Field label="Status" value={annotations[AWS_ACCOUNT_STATUS]} />
      <Owners
        label="Business Owners"
        values={tokens(annotations[BUSINESS_OWNERS])}
      />
      <Owners
        label="Technical Owners"
        values={tokens(annotations[TECHNICAL_OWNERS])}
      />
      <Owners
        label="Project Managers"
        values={tokens(annotations[PROJECT_MANAGERS])}
      />
    </InfoCard>
  );
};
